// Copyright (c) Ping. All rights reserved.
//
// One-shot helper to extract `ownDeviceId` from the MLS SDK ahead of
// `/v1/auth/verify`.
//
// Why this exists: ping-openmls-sdk derives the device id internally
// from the identity seed (private to the wasm worker). The BE's
// auth layer historically derived `device_id = SHA256(device_signing_pubkey)`
// — those are completely unrelated bytes. Result: every envelope the
// SDK emits has `sender_device = sdk_own_device_id`, but the BE's
// JWT `device_id` claim is `SHA256(pubkey)`, so `POST /v1/messages`
// rejects every envelope with 400 `sender_device_mismatch`.
//
// Fix (Option A, BE side ships in `auth/types.go` + `auth/device.go` +
// `auth/store.go`): `/v1/auth/verify` now accepts an optional
// `device_id` field. When supplied, the BE uses those bytes directly
// as the JWT's `device_id` claim instead of `SHA256(pubkey)`. The
// `device_binding_sig` must sign over the supplied bytes.
//
// To send the SDK's own device id at verify time we need to know it
// before we have an authenticated session — chicken-and-egg with the
// real session bootstrap. We resolve it by spinning up a
// `MessagingClient` with a no-op transport (the SDK only needs the
// transport for send/subscribe; `deviceId()` reads cached state on
// init). The temporary client is discarded; the real client gets
// re-init'd in `bootstrapWebSession` with the persistent transport.

import { log } from '@core/log/log';

// STATUS: pre-init is currently disabled.
//
// Goal: at /v1/auth/verify time, tell the BE the SDK's `ownDeviceId`
// so the JWT's `device_id` claim matches the `sender_device` field
// the SDK writes into every envelope. The BE-side support is in
// place (see services/api/internal/auth/types.go `DeviceID` field).
//
// Why it's off:
//   1. EphemeralStorage works — no corruption — but the SDK
//      generates a FRESH random device id every time on empty
//      storage. Pre-init's id (random) ≠ real bootstrap's id (also
//      random, but persisted on first encounter with PingStorageWeb).
//      Diagnostic: `session.bootstrap.deviceIdDiag` shows match: false.
//   2. Sharing PingStorageWeb between pre-init and real bootstrap
//      would let the second client read the first client's
//      persisted state — but the SDK throws `StorageCorruption:
//      Failed to decode …` on the second `MessagingClient.init`,
//      blocking sign-in entirely.
//
// Neither client-side strategy works against the current SDK 0.2.0.
// Production fix needs one of:
//   a) SDK accepts an `ownDeviceId` override in `ClientConfig`, so
//      both pre-init (if ever needed again) and real bootstrap use
//      the same supplied bytes.
//   b) message-service relaxes `expected_sender_device` validation
//      (auth via JWT account_id is sufficient; envelope's sender
//      device is metadata for MLS routing, not auth).
//   c) `POST /v1/auth/rebind-device-id` endpoint that lets the
//      client update the JWT's `device_id` AFTER bootstrap reads
//      the SDK's persisted `ownDeviceId`.
//
// Until one of those lands, the BE's `device_id` field path is
// still functional but unused; we fall back to legacy SHA-256(pubkey)
// derivation and accept that `POST /v1/messages` rejects with 400
// `invalid_request` (sender_device_mismatch) until the BE-side
// validation is relaxed.

/**
 * Disabled: returns `null` unconditionally. See the file header for
 * the design discussion. Callers fall back to the legacy
 * `SHA-256(device_signing_pubkey)` derivation when this returns
 * null, which means the BE's JWT `device_id` claim will not match
 * the SDK's envelope `sender_device` field — and `POST /v1/messages`
 * will reject with 400 until message-service is relaxed.
 *
 * Kept as a function (rather than removed) so the wiring on the
 * caller side (`useAuthFlowCoordinator.verifyCode`) stays in place
 * for the day one of the three production fixes above lands.
 */
export async function getSdkDeviceIdBytes(): Promise<Uint8Array | null> {
  log.info({
    event: 'preauth.sdkDeviceId.disabled',
    reason: 'sdk_storage_alignment_unsupported_in_sdk_0.2.0',
  });
  return null;
}
